import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ConstructionIssuesService } from '../services/issues.service';
import type { ConstructionIssue, IssueFilters, IssueComment } from '../types/construction-issues.types';
import { useState } from 'react';
import { toast } from 'sonner';

export function useConstructionIssues(companyId: string, initialFilters: IssueFilters = {}) {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<IssueFilters>({
    status: 'all',
    severity: 'all',
    ...initialFilters
  });

  const query = useQuery({
    queryKey: ['construction-issues', companyId, filters],
    queryFn: () => ConstructionIssuesService.getIssues(companyId, filters),
    enabled: !!companyId,
  });

  const statsQuery = useQuery({
    queryKey: ['construction-issue-stats', companyId, filters.projectId],
    queryFn: () => ConstructionIssuesService.getIssueStats(companyId, filters.projectId),
    enabled: !!companyId,
  });

  const createMutation = useMutation({
    mutationFn: (data: Partial<ConstructionIssue>) => ConstructionIssuesService.createIssue(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['construction-issues'] });
      toast.success('Báo cáo sự cố thi công thành công');
    },
  });

  return {
    issues: query.data?.data || [],
    total: query.data?.total || 0,
    stats: statsQuery.data,
    isLoading: query.isLoading || statsQuery.isLoading,
    filters,
    setFilters,
    createIssue: createMutation.mutateAsync,
    isCreating: createMutation.isPending,
  };
}

export function useConstructionIssueDetail(id?: string) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['construction-issues', 'detail', id],
    queryFn: () => ConstructionIssuesService.getIssueDetail(id!),
    enabled: !!id,
  });

  const commentsQuery = useQuery({
    queryKey: ['construction-issues', 'comments', id],
    queryFn: () => ConstructionIssuesService.getIssueComments(id!),
    enabled: !!id,
  });

  const updateMutation = useMutation({
    mutationFn: (updates: Partial<ConstructionIssue>) => ConstructionIssuesService.updateIssue(id!, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['construction-issues'] });
      queryClient.invalidateQueries({ queryKey: ['construction-issues', 'detail', id] });
      toast.success('Cập nhật sự cố thành công');
    },
  });

  return {
    issue: query.data,
    comments: commentsQuery.data || [],
    isLoading: query.isLoading || commentsQuery.isLoading,
    updateIssue: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
  };
}
